import { FunctionComponent, ReactElement, useState } from "react";

import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
    styled,
} from "@mui/material";

import { LoadingButton } from "@mui/lab";
import {
    DataGrid,
    GridColDef,
    GridPaginationModel,
    GridRenderCellParams,
} from "@mui/x-data-grid";
import { format } from "date-fns";

import {
    useGetIngredientCategories,
    useGetIngredients,
    useGetUnmatchedIngredients,
    useResolveUnmatchedIngredient,
} from "../../hooks";
import { useMessage } from "../../sdk/hooks";
import {
    IIngredient,
    IngredientBaseUnit,
    IUnmatchedIngredientName,
} from "../../sdk/types/api";
import { globalDateTimeFormat } from "../../utils/constants";
import { ingredientBaseUnitNames, ingredientQueryKeys } from "../ingredients/constants";

const Root = styled("section")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};

    padding: ${({ theme }) => theme.spacing(1)};
`;

const Header = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.5)};

    padding: ${({ theme }) => theme.spacing(2, 1)};
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const Subtitle = styled(Typography)`
    font-size: 14px;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const Fields = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};

    padding-top: ${({ theme }) => theme.spacing(1)};
`;

type TResolveMode = "map" | "create";

export const UnmatchedIngredientsScreen: FunctionComponent = (): ReactElement => {
    const { showError, showSuccess } = useMessage();
    const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    const [target, setTarget] = useState<{ row: IUnmatchedIngredientName; mode: TResolveMode } | null>(null);

    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState<IIngredient | null>(null);
    const [newName, setNewName] = useState("");
    const [newCategoryId, setNewCategoryId] = useState("");
    const [newBaseUnit, setNewBaseUnit] = useState<IngredientBaseUnit>(IngredientBaseUnit.GRAM);

    const unmatchedQuery = useGetUnmatchedIngredients(
        [ingredientQueryKeys.unmatched, pagination.page, pagination.pageSize],
        { page: pagination.page + 1, limit: pagination.pageSize },
        { keepPreviousData: true },
    );
    const categoriesQuery = useGetIngredientCategories(ingredientQueryKeys.categories);
    const searchQuery = useGetIngredients(
        [ingredientQueryKeys.list, "picker", search],
        { q: search || undefined, page: 1, limit: 20 },
        { enabled: target?.mode === "map", keepPreviousData: true },
    );
    const resolveMutation = useResolveUnmatchedIngredient(ingredientQueryKeys.unmatched);

    const open = (row: IUnmatchedIngredientName, mode: TResolveMode) => {
        setTarget({ row, mode });
        setSearch(mode === "map" ? row.normalizedName : "");
        setSelected(null);
        setNewName(row.rawName.charAt(0).toUpperCase() + row.rawName.slice(1));
        setNewCategoryId("");
        setNewBaseUnit(IngredientBaseUnit.GRAM);
    };

    const handleResolve = async () => {
        if (!target) {
            return;
        }
        try {
            const body =
                target.mode === "map"
                    ? { ingredientId: selected?.id ?? "" }
                    : {
                          ingredient: {
                              name: newName.trim(),
                              categoryId: newCategoryId || null,
                              baseUnit: newBaseUnit,
                          },
                      };
            const ingredient = await resolveMutation.mutateAsync({ unmatchedId: target.row.id, body });
            showSuccess(`"${target.row.rawName}" now maps to ${ingredient.name}`);
            setTarget(null);
        } catch (error) {
            showError(error);
        }
    };

    const columns: GridColDef<IUnmatchedIngredientName>[] = [
        { field: "rawName", headerName: "Name in recipe", flex: 1 },
        { field: "occurrences", headerName: "Seen", flex: 0.3 },
        {
            field: "lastSeenAt",
            headerName: "Last seen",
            flex: 0.6,
            valueGetter: ({ row }) => format(new Date(row.lastSeenAt), globalDateTimeFormat),
        },
        {
            field: "actions",
            headerName: "",
            flex: 0.8,
            sortable: false,
            renderCell: ({ row }: GridRenderCellParams<IUnmatchedIngredientName>) => (
                <>
                    <Button size="small" onClick={() => open(row, "map")}>
                        Map to existing
                    </Button>
                    <Button size="small" onClick={() => open(row, "create")}>
                        Create ingredient
                    </Button>
                </>
            ),
        },
    ];

    const canSubmit = target?.mode === "map" ? Boolean(selected) : Boolean(newName.trim());

    return (
        <Root>
            <Header>
                <Title>Unmatched ingredient names</Title>
                <Subtitle>
                    Names used in recipes that aren't in the catalog. They appear as separate lines
                    under "Other" on shopping lists until they are mapped.
                </Subtitle>
            </Header>

            <DataGrid
                autoHeight
                rows={unmatchedQuery.data?.records ?? []}
                rowCount={unmatchedQuery.data?.total ?? 0}
                columns={columns}
                loading={unmatchedQuery.isLoading || unmatchedQuery.isFetching}
                paginationMode="server"
                paginationModel={pagination}
                onPaginationModelChange={setPagination}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                disableColumnSelector
            />

            <Dialog open={Boolean(target)} onClose={() => setTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {target?.mode === "map" ? "Map" : "Create ingredient for"} "{target?.row.rawName}"
                </DialogTitle>
                <DialogContent>
                    {target?.mode === "map" ? (
                        <Fields>
                            <Autocomplete
                                options={searchQuery.data?.records ?? []}
                                getOptionLabel={(option) => option.name}
                                filterOptions={(options) => options}
                                value={selected}
                                onChange={(_event, value) => setSelected(value)}
                                inputValue={search}
                                onInputChange={(_event, value) => setSearch(value)}
                                loading={searchQuery.isFetching}
                                renderInput={(inputParams) => (
                                    <TextField {...inputParams} size="small" label="Ingredient" autoFocus />
                                )}
                            />
                            <Subtitle>The name is added to the ingredient's aliases.</Subtitle>
                        </Fields>
                    ) : (
                        <Fields>
                            <TextField
                                label="Name"
                                size="small"
                                value={newName}
                                onChange={(event) => setNewName(event.target.value)}
                            />
                            <TextField
                                select
                                label="Category"
                                size="small"
                                value={newCategoryId}
                                onChange={(event) => setNewCategoryId(event.target.value)}
                            >
                                <MenuItem value="">Other</MenuItem>
                                {(categoriesQuery.data ?? []).map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {`${category.icon ?? ""} ${category.name}`.trim()}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                label="Base unit"
                                size="small"
                                value={newBaseUnit}
                                onChange={(event) => setNewBaseUnit(Number(event.target.value))}
                            >
                                {[IngredientBaseUnit.GRAM, IngredientBaseUnit.MILLILITRE, IngredientBaseUnit.PIECE].map(
                                    (unit) => (
                                        <MenuItem key={unit} value={unit}>
                                            {ingredientBaseUnitNames[unit]}
                                        </MenuItem>
                                    ),
                                )}
                            </TextField>
                        </Fields>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTarget(null)}>Cancel</Button>
                    <LoadingButton
                        variant="contained"
                        disabled={!canSubmit}
                        loading={resolveMutation.isLoading}
                        onClick={handleResolve}
                    >
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Root>
    );
};
